/**
 * Pivot levels for Fuzion Levels, derived from the close and the expected move.
 *
 * These are NOT fetched. The levels payload carries no `r1`/`s1` fields — the
 * six pivots are arithmetic on two figures it already provides, and this is the
 * same derivation the TradingView script performs:
 *
 *   R1 / S1  =  close ± 0.5 × expected move
 *   R2 / S2  =  close ±       expected move
 *   R3 / S3  =  close ± 1.5 × expected move
 *
 * ⚠️ **The expected move is the 2-sigma band, not the 1-sigma one.** R2/S2 are
 * the expected move itself, so R1/S1 sit *inside* it at half, and R3/S3 outside
 * at one and a half. Reading `expected_move` as R1 would put every level in the
 * wrong place while still looking like a plausible ladder.
 */

export interface PivotLevels {
  /** Below the close, nearest first. */
  s1: number;
  s2: number;
  s3: number;
  /** Above the close, nearest first. */
  r1: number;
  r2: number;
  r3: number;
}

/** Multipliers applied to the expected move, in ladder order. */
const PIVOT_STEPS = { one: 0.5, two: 1, three: 1.5 } as const;

/**
 * The six pivots, or null when either input is missing.
 *
 * ⚠️ Null rather than zeros. A pivot ladder collapsed onto the close is not a
 * neutral default — it reads as six real levels that happen to coincide, which
 * is a far more misleading thing to draw than an empty cell.
 *
 * ⚠️ The expected move is used as a MAGNITUDE. It describes a distance either
 * side of the close, so a negative arriving on the wire would otherwise invert
 * the ladder and print supports above resistances.
 */
export function pivotLevels(
  close: number | null | undefined,
  expectedMove: number | null | undefined
): PivotLevels | null {
  if (close == null || expectedMove == null) return null;
  if (!Number.isFinite(close) || !Number.isFinite(expectedMove)) return null;

  const move = Math.abs(expectedMove);
  if (move === 0) return null;

  return {
    s1: close - move * PIVOT_STEPS.one,
    s2: close - move * PIVOT_STEPS.two,
    s3: close - move * PIVOT_STEPS.three,
    r1: close + move * PIVOT_STEPS.one,
    r2: close + move * PIVOT_STEPS.two,
    r3: close + move * PIVOT_STEPS.three,
  };
}


/** The sigma bands as they arrive on `expected_move`. */
export interface SigmaBands {
  sigma_1_up: number | null;
  sigma_1_down: number | null;
  sigma_2_up: number | null;
  sigma_2_down: number | null;
}

/**
 * The close the ladder is built around — from OHLC, or recovered from the
 * sigma bands when OHLC is absent.
 *
 * ⚠️ **`ohlc` and `expected_move` come from different sources and fail
 * independently.** In practice the expected move arrives while OHLC is null,
 * which left the rail with no centre and every row reading "No levels" beside
 * a perfectly good ±figure.
 *
 * The bands are absolute prices straddling the close, so their MIDPOINT is the
 * close. Either pair works and both should agree; the 1-sigma pair is tried
 * first only because it is the narrower of the two, so a rounding error in the
 * feed moves the result less.
 *
 * ⚠️ Returns null rather than guessing from a single band. One side alone
 * cannot locate the centre without already knowing the move, and inferring it
 * from `expected_move` would make the close depend on the very figure the
 * ladder is measured in — any error would then be invisible, because every
 * level would be wrong by the same amount and still look symmetrical.
 */
export function resolveClose(
  ohlcClose: number | null | undefined,
  bands: SigmaBands | null | undefined
): number | null {
  if (ohlcClose != null && Number.isFinite(ohlcClose)) return ohlcClose;
  if (!bands) return null;

  const mid = (a: number | null, b: number | null): number | null =>
    a != null && b != null && Number.isFinite(a) && Number.isFinite(b) ? (a + b) / 2 : null;

  return mid(bands.sigma_1_up, bands.sigma_1_down) ?? mid(bands.sigma_2_up, bands.sigma_2_down);
}
